import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { LanguageCode } from '~/shared/internationalization';

import type {
    AiTransformationItem,
    AiTransformationMatrixGrouping,
} from '../../../data-contract/ai-transformation-strategy';
import type {
    AiTransformationFetchers,
    AiTransformationFilters,
} from '../../../fetchers/position-management/ai-transformation-fetchers';
import { unwrapResult } from '../../../fetchers/unwrap-result';
import { useApplicationFetchers } from '../../../fetchers/use-application-fetchers';
import { POSITION_DERIVED_QUERY_META } from '../../../position-management';
import { useLocale } from '../../components/use-locale';
import { priorityCoverage } from '../model/prioritization';
import { strategyKeys } from './query-keys';

/**
 * A tile's subject as the strategy has it: whether it counts as prioritized, as its list row's checkbox shows it, and
 * whether the caller may change that now. A position or task also gives its item, which a position's prioritization
 * takes its tasks along by.
 */
export type TilePriority = {
    readonly canChange: boolean;
    readonly isPrioritized: boolean;
    readonly item: AiTransformationItem | undefined;
};

type TilePriorityRequest = {
    readonly acceptLanguage: LanguageCode;
    readonly architectureId: string;
    readonly filters: AiTransformationFilters;
    readonly id: string;
    readonly signal: AbortSignal;
};

/** An item counts as prioritized from Prioritized on; only Idle and Prioritized change by hand. */
const canChangeItem = (item: AiTransformationItem): boolean =>
    item.status === 'IDLE'
        ? item.movableTo.includes('PRIORITIZED')
        : item.status === 'PRIORITIZED' && item.movableTo.includes('IDLE');

/**
 * Whether the caller may set or take back Prioritized on an item at all, which needs Job Architecture edit rights; a
 * Transformation Lead may move their own items to other statuses, but never into or out of Prioritized.
 */
const showsPriorityTo = (item: AiTransformationItem): boolean =>
    canChangeItem(item) || item.movableTo.includes('PRIORITIZED');

/**
 * One position or task in exactly two requests: where it ranks in its list, then the one-item page at that rank. Nothing
 * when the strategy does not hold it or the caller may not set priorities on it.
 */
const readItem = async (
    fetchers: AiTransformationFetchers,
    subject: 'POSITION' | 'TASK',
    { acceptLanguage, architectureId, filters, id, signal }: TilePriorityRequest,
): Promise<TilePriority | null> => {
    const location = await unwrapResult(
        fetchers.fetchAiTransformationLocation({
            acceptLanguage,
            architectureId,
            filters,
            id,
            signal,
            view: subject === 'TASK' ? 'TASK' : 'ROLE',
        }),
    );
    if (location.index === null) return null;
    const page = await unwrapResult(
        fetchers.fetchAiTransformationItems({
            acceptLanguage,
            architectureId,
            filters,
            node: subject === 'TASK' ? undefined : location.nodeKeys.at(-1),
            page: location.index,
            signal,
            size: 1,
            sort: subject === 'TASK' ? undefined : 'TITLE',
            subject,
        }),
    );
    const item = page.items.find((candidate) => candidate.id === id);
    if (item === undefined || !showsPriorityTo(item)) return null;

    return { canChange: canChangeItem(item), isPrioritized: item.status !== 'IDLE', item };
};

/**
 * A role or level in exactly two requests at once: the counts of its positions, which say how far it is prioritized,
 * and one of its positions still Idle or Prioritized, which says whether the caller may change that. Nothing when it
 * has no position the caller may prioritize or take back.
 */
const readGroup = async (
    fetchers: AiTransformationFetchers,
    grouping: 'LEVEL' | 'ROLE',
    { acceptLanguage, architectureId, filters, id, signal }: TilePriorityRequest,
): Promise<TilePriority | null> => {
    const groupFilters = grouping === 'ROLE' ? { ...filters, roleId: [id] } : { ...filters, levelId: [id] };
    const [summary, sample] = await Promise.all([
        unwrapResult(
            fetchers.fetchAiTransformationSummary({
                acceptLanguage,
                architectureId,
                filters: groupFilters,
                signal,
                subject: 'POSITION',
            }),
        ),
        unwrapResult(
            fetchers.fetchAiTransformationItems({
                acceptLanguage,
                architectureId,
                filters: groupFilters,
                page: 0,
                signal,
                size: 1,
                statuses: ['IDLE', 'PRIORITIZED'],
                subject: 'POSITION',
            }),
        ),
    ]);
    const movable = sample.items[0];
    if (summary.total === 0 || movable === undefined || !canChangeItem(movable)) return null;

    return {
        canChange: true,
        isPrioritized: priorityCoverage(summary.statusCounts, summary.total) === 'full',
        item: undefined,
    };
};

/**
 * Whether a tile's position, task, role or level is prioritized. Each read is a fixed two requests, never retried, and
 * stops with the tile; it refreshes with the rest of the architecture's strategy reads after every priority change.
 */
export const useTilePriority = ({
    architectureId,
    filters,
    id,
    kind,
}: {
    readonly architectureId: string | undefined;
    readonly filters: AiTransformationFilters;
    readonly id: string;
    readonly kind: AiTransformationMatrixGrouping;
}): UseQueryResult<TilePriority | null, Error> => {
    const fetchers = useApplicationFetchers();
    const acceptLanguage = useLocale();

    return useQuery({
        enabled: architectureId !== undefined,
        meta: POSITION_DERIVED_QUERY_META,
        queryFn: ({ signal }) => {
            const request = { acceptLanguage, architectureId: architectureId ?? '', filters, id, signal };
            return kind === 'POSITION' || kind === 'TASK'
                ? readItem(fetchers, kind, request)
                : readGroup(fetchers, kind, request);
        },
        queryKey: strategyKeys.tilePriority(architectureId ?? '', { filters, id, kind }, acceptLanguage),
        retry: false,
        throwOnError: false,
    });
};
